import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Typography } from 'components/shared/Typography';

import { PlatformChip } from 'components/PlatformChip';
import { StatusTag } from 'components/StatusTag';

import { SessionStrip } from '../SessionStrip';

import {
  EXIT_CAUSE_ICONS,
  EXIT_CAUSE_TAG_VARIANTS,
  formatDuration,
  formatSessionTime,
  getExitCauseLabel,
  getSessionExitScreenLabel,
  type SessionSummary,
} from '../../TimelineSection.helpers';

import styles from './SessionListItem.module.scss';

const SessionListItem = ({
  selected,
  session,
  onSelect,
}: {
  selected: boolean;
  session: SessionSummary;
  onSelect: (sessionId: string) => void;
}) => {
  const { i18n } = useTranslation();
  const exitScreenLabel = getSessionExitScreenLabel(session);

  return (
    <button
      type="button"
      className={cn(styles.item, { [styles.itemSelected]: selected })}
      aria-current={selected}
      data-session-id={session.id}
      onClick={() => onSelect(session.id)}
    >
      <div className={styles.top}>
        <Typography
          className={styles.time}
          component="span"
          variant="body2"
          weight="semiBold"
          mode="compact"
        >
          {formatSessionTime(session.startedAt, i18n.language)}
        </Typography>
        <StatusTag
          variant={EXIT_CAUSE_TAG_VARIANTS[session.exitCause]}
          icon={EXIT_CAUSE_ICONS[session.exitCause]}
        >
          {getExitCauseLabel(session.exitCause)}
        </StatusTag>
      </div>

      <div className={styles.meta}>
        <PlatformChip platform={session.platform} />
        <Typography
          className={styles.metaText}
          component="span"
          variant="body3"
          mode="compact"
        >
          {formatDuration(session.durationMs)}
        </Typography>
        <span className={styles.metaDot} aria-hidden />
        <Typography
          className={styles.metaText}
          component="span"
          variant="body3"
          mode="compact"
        >
          {getTranslation('student_page.journey.list.actions', {
            count: session.actionsCount,
          })}
        </Typography>
      </div>

      <div className={styles.exit}>
        <Typography
          className={styles.exitLabel}
          component="span"
          variant="body3"
          mode="compact"
        >
          {getTranslation('student_page.journey.list.exit_screen')}
        </Typography>
        <Typography
          className={styles.exitScreen}
          component="span"
          variant="body3"
          weight="medium"
          mode="compact"
          title={exitScreenLabel}
        >
          {exitScreenLabel}
        </Typography>
      </div>

      <SessionStrip highlighted={selected} strip={session.strip} />
    </button>
  );
};

export default React.memo(SessionListItem) as any;
